import { TranslateService } from '@ngx-translate/core';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import { merge } from 'lodash';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ValueHelperInstanceToken } from '../../interfaces/value-helper.interface';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import { CompositeModel } from '../complex/composite.model';
import { RadioModel } from '../complex/radio.model';
import { NotificationModel } from '../low-level/notification.model';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { RadioModelOptions } from '../options/radio-model-options';
import { TextModel } from '../primitive/text.model';
import { ArrayModel } from './array.model';
import { CrmCodeModel, ODataCrmCodeOutModel } from './crm-code.model';

export class ConfirmationUploadToDocboxModel extends CompositeModel {
    public uploadToDocBoxOptions: RadioModel<TextModel>;
    public readonly isMandatory: boolean;
    private readonly codes: ValueHelperModel[];
    public constructor(
        public documentTitle: string[] = [],
        notification: NotificationModel,
    ) {
        super('ConfirmationUploadToDocboxModel');

        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigInstanceToken);
        const notificationUtilities = injector.get(NotificationUtilities);
        const translate = injector.get(TranslateService);
        const valueHelperService = injector.get(ValueHelperInstanceToken);
        this.codes = valueHelperService.getUploadToDocboxValueHelpers();
        const two = 2;
        if (this.codes.length !== two) {
            throw new Error(
                `ConfirmationUploadToDocboxModel: Invalid number of value helpers: ${this.codes.length} for Yes/No radio options`,
            );
        }

        const optionGroup: Array<DisplayDecorator<TextModel>> = [];
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_UPLOAD_TO_DOCBOX_OPTION_YES'),
                new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_UPLOAD_TO_DOCBOX_OPTION_NO'),
                new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_UPLOAD_TO_DOCBOX_OPTION_NA'),
                new TextModel(ConfigConstants.optionNA, ConfigConstants.optionValueNA),
            ),
        );

        // upload of training's attendees' signatures is mandatory for notification with type 'MI' and subtype 'IHOT' for France
        this.isMandatory =
            configService.getUploadToDocBoxMandatoryForNotificationType(notification.notificationType) ||
            (notification.salesOrg?.includes(ConfigConstants.configKeyFrance) &&
                notificationUtilities.isInstallationNotificationWithIHOT(notification));

        const radioIsReadonly = this.documentTitle.length > 0;
        const initialSelection = !this.isMandatory && !radioIsReadonly ? 1 : two;

        this.uploadToDocBoxOptions = new RadioModel<TextModel>(
            'UploadToDocBox',
            optionGroup,
            new RadioModelOptions({
                initialSelection: initialSelection,
                invalidSelection: 2,
                readonly: radioIsReadonly,
            }),
        );
    }

    public toJSON(): unknown {
        return {
            uploadToDocBoxSelection: this.uploadToDocBoxOptions.selectedEntry.model.value,
            documentTitle: this.documentTitle,
        };
    }

    public getOutModel(): ODataConfirmationUploadToDocboxOutModel {
        const outModel = super.getOutModel();
        const selectedOption =
            this.uploadToDocBoxOptions.selectedEntry.value === ConfigConstants.optionValueYes
                ? this.codes[0]
                : this.codes[1];
        const crmCodes = <ArrayModel<CrmCodeModel>>this.set(new ArrayModel<CrmCodeModel>('CrmCodesSet'));
        crmCodes.addModel(
            new CrmCodeModel({
                codeType: 'A',
                qKatArt: '',
                codeGroup: selectedOption.codeLevel,
                code: selectedOption.code,
                codeText: selectedOption.descText,
            }),
        );
        merge(outModel, crmCodes.getOutModel());
        return <ODataConfirmationUploadToDocboxOutModel>outModel;
    }
}

export class ConfirmationUploadToDocboxModelFromJson extends ConfirmationUploadToDocboxModel {
    public constructor(data: JsonConfirmationUploadToDocboxModel, notification: NotificationModel) {
        super(data.documentTitle, notification);
        if (data.uploadToDocBoxSelection === ConfigConstants.optionValueYes && data.documentTitle.length === 0) {
            // Select back N/A, if only the option yes was selected and got saved because crm was saved, without the document being uploaded
            this.uploadToDocBoxOptions.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueNA);
        } else {
            this.uploadToDocBoxOptions.selectByPredicate((entry) => entry.value === data.uploadToDocBoxSelection);
        }
    }
}

export interface JsonConfirmationUploadToDocboxModel {
    uploadToDocBoxSelection: string;
    documentTitle: string[];
}

export interface ODataConfirmationUploadToDocboxOutModel extends ODataObject {
    CrmCodesSet: ODataCrmCodeOutModel[];
}
